<template>
  <section>
    <v-card :loading="isGetWebhookConfigPending || isFindWebhooksPending">
      <v-card-title>
        <span class="text-h4">
          Webhook
          <span class="pl-3 subtitle-1">Settings</span>
        </span>
      </v-card-title>
      <v-card-subtitle class="pl-5 body-1"
        >You can setup a webhook to use in your actions. Webhooks will return
        messages and elaborator data which you can use in your own apps amd
        systems.</v-card-subtitle
      >
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" md="6">
              <p class="body-1">Last 5 webhook requests.</p>
              <v-card flat v-for="webhook in webhooks" :key="webhook.id">
                <v-card-title>
                <v-btn v-if="!webhook.success && !webhook.retry" fab small absolute right title="Retry" @click="retryWebhook(webhook)"
                  ><v-icon>mdi-refresh</v-icon></v-btn
                >
                {{
                  (webhook.action && webhook.action.name) || webhook.type | title_case
                }}</v-card-title>
                <v-card-subtitle>
                  <div v-if="!webhook.success">
                    <v-icon color="error">mdi-alert-circle</v-icon>Failed
                  </div>
                  <div v-if="webhook.success">
                    <div><v-icon color="success">mdi-check</v-icon>Succeeded</div>
                  </div>
                  <div class="d-flex justify-space-between mt-2 text--primary">
                    <div>Created: {{ webhook.created_at | date_time }}</div>
                    <div v-if="webhook.sent_at">Sent: {{ webhook.sent_at | date_time }}</div>
                  </div>
                </v-card-subtitle>
                <v-card-text>
                  <div v-if="webhook.error">
                    <p class="body-1">Error:</p>
                    <p class="body-2">{{ webhook.error }}</p>
                  </div>
                  <v-expansion-panels flat>
                    <v-expansion-panel>
                      <v-expansion-panel-header class="body-1"
                        >Webhook Data</v-expansion-panel-header
                      >
                      <v-expansion-panel-content class="grey darken-2 pt-3">
                        <pre>{{ webhook.data }}</pre>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
                <v-divider></v-divider>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <p class="body-1">
                Your webhook URL should be a POST endpoint that accepts JSON
                data.
              </p>
              <v-alert border="left" color="error" v-if="webhookUrlError">{{
                webhookUrlError
              }}</v-alert>
              <v-text-field
                label="URL*"
                hint="Your webhook URL"
                v-model="webhookUrl"
                :rules="urlRules"
                :prepend-icon="webhookIcon"
                validate-on-blur
                required
              ></v-text-field>
              <div class="mt-4 d-flex justify-end">
                <v-btn color="primary" outlined @click="setWebhook()"
                  >Set Webhook</v-btn
                >
              </div>
              <p class="body-1 mt-12">
                To test the member registration webhook enter the email of an
                existing member.
              </p>
              <v-text-field
                label="Email*"
                hint="Member email to send registration webhook for"
                v-model="registrationWebhookEmail"
                prepend-icon="mdi-email"
                validate-on-blur
                required
              ></v-text-field>
              <div class="mt-4 d-flex justify-end">
                <v-btn
                  color="primary"
                  outlined
                  @click="testRegistrationWebhook()"
                  >Test Registration Webhook</v-btn
                >
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions></v-card-actions>
    </v-card>
  </section>
</template>

<script>
import axios from "axios";
import { mapActions } from "vuex";
import { makeGetMixin, makeFindMixin, models } from "feathers-vuex";

export default {
  data: () => ({
    loading: false,
    isWebhookValid: false,
    configId: "webhook",
    webhookUrlError: null,
    registrationWebhookEmail: null,
    urlRules: [
      (v) =>
        v === undefined ||
        (v && /(https?):\/\/(-\.)?([^\s/?.#-]+\.?)+(\/[^\s]*)?$/i.test(v)) ||
        'Invalid URL, please use "https://mydomain.ext/webhook" format when entering URL',
    ],
  }),
  mixins: [
    makeGetMixin({
      service: "configuration",
      id: "configId",
      name: "webhookConfig",
    }),
    makeFindMixin({ service: "webhooks" }),
  ],
  created() {},
  mounted() {},
  computed: {
    webhookConfigParams() {
      return {
        query: {},
      };
    },
    webhooksParams() {
      return {
        query: {
          $limit: 5,
          $sort: {
            created_at: -1,
          },
          $eager: "[action]",
        },
      };
    },
    webhookUrl: {
      get() {
        return (
          this.webhookConfig &&
          this.webhookConfig.config &&
          this.webhookConfig.config.url
        );
      },
      set(url) {
        if (this.webhookConfig && this.webhookConfig.config)
          this.webhookConfig.config.url = url;
        else this.webhookConfig = { config: { url: url } };
      },
    },
    webhookIcon() {
      return this.webhookUrl || this.isWebhookValid
        ? "mdi-check-network-outline"
        : "mdi-close-network-outline";
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    async setWebhook() {
      await this.urlExists();

      if (this.isWebhookValid) {
        this.webhookConfig.save();
      }
    },
    async retryWebhook(webhook) {
      webhook.retry = true;
      webhook.save();
    },
    async testRegistrationWebhook() {
      const { Webhook } = models.bot;
      const registrationWebhook = new Webhook({
        registrationWebhookEmail: this.registrationWebhookEmail,
      });
      try {
        await registrationWebhook.create();
        this.showMessage({
          text: "Successfully sent registration test webhook!",
          position: "top",
          color: "success",
        });
        this.registrationWebhookEmail = null;
      } catch (error) {
        this.showMessage({
          text:
            error.message || "Error occured sending registration test webhook",
          position: "top",
          color: "error",
        });
      }
    },
    async urlExists() {
      try {
        let response = await axios({
          url: this.webhookConfig.config.url,
          method: "HEAD",
        });
        this.isWebhookValid = /2\d\d/.test(response.status) === true;

        if (this.isWebhookValid)
          this.showMessage({
            text: "Successfully set up webhook!",
            position: "top",
            color: "success",
          });
        else
          this.webhookUrlError =
            "Expected a status code response of 2xx, URL not valid.";
      } catch (error) {
        this.webhookUrlError = error.message;
        this.isWebhookValid = false;
      }
    },
  },
  watch: {
    "webhook.config.url": {
      handler() {
        this.webhookUrlError = null;
      },
    },
    webhooks(now) {
      if (now.length) this.findWebhooks();
    },
  },
  props: {
    data: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
};
</script>

<style lang="scss" scoped>
</style>